import { AlertTriangle } from "lucide-react";
import { useTranslation } from "react-i18next";

interface HintsSection {
  title: string;
  items?: string[];
  content?: string;
  numbered?: boolean;
}

interface HintsStepProps {
  sections?: HintsSection[];
  warning?: string;
}

const HintsStep = ({ sections, warning }: HintsStepProps) => {
  const { t } = useTranslation();

  const displaySections =
    sections ||
    (t("book_location.hints.sections", { returnObjects: true }) as HintsSection[]);
  const displayWarning = warning ?? t("book_location.hints.warning");

  return (
    <div className="px-5 space-y-6 mb-5">
      {displaySections.map((section, sectionIndex) => (
        <div key={sectionIndex}>
          <h3 className="text-sm font-semibold text-foreground mb-3">{section.title}</h3>
          {section.content ? (
            <p
              className="text-foreground text-sm leading-relaxed"
              dangerouslySetInnerHTML={{ __html: section.content }}
            />
          ) : section.numbered ? (
            <ol className="space-y-3">
              {section.items?.map((item, index) => (
                <li key={index} className="flex gap-3 items-start">
                  <span className="flex items-center justify-center w-7 h-7 rounded-full bg-primary text-primary-foreground text-xs font-semibold shrink-0">
                    {index + 1}
                  </span>
                  <p className="text-foreground text-sm pt-0.5">{item}</p>
                </li>
              ))}
            </ol>
          ) : (
            <ul className="space-y-2">
              {section.items?.map((item, index) => (
                <li key={index} className="flex gap-2 items-baseline">
                  <span className="text-muted-foreground">•</span>
                  <p className="text-foreground text-sm">{item}</p>
                </li>
              ))}
            </ul>
          )}
        </div>
      ))}

      {displayWarning && (
        <div className="p-3 bg-amber-50 border border-amber-200 rounded-lg flex gap-2 items-start">
          <AlertTriangle className="w-4 h-4 text-amber-600 flex-shrink-0 mt-0.5" />
          <p className="text-sm text-amber-700">{displayWarning}</p>
        </div>
      )}
    </div>
  );
};

export default HintsStep;
